import { MarketOutcome } from '../../enums';
import { GameContext, MarketConfig } from '../../interfaces';
import {
	extractNumber,
	extractSelection,
	isAbnormalFixtureEnd,
	isCancelledFixture,
	isWalkoverFixture,
	resolveConditionAtEnd,
	resolveGamePlayerProp,
	retirementWinnerSide,
	roundThresholdUp,
} from '../../utils';

// Tennis payload shape (verified 2026-07-02 against a completed OpticOdds ATP
// Wimbledon fixture, id 20260702739C7FA3, and cross-checked against getGameContext
// in market.utils.ts which reads the SAME stored SSE payload for MLB/soccer/etc):
//
//   result.score.scores.home.total          → sets won by home player
//   result.score.scores.home.periods.<key>  → games won in set (period_1, period_2, ...)
//   result.score.stats.home                 → Array<{ period: 'all' | 'period_N', stats }>
//                                              containing aces, double_faults, tiebreaks_won, etc.
//   result.player_results[i].player.name    → full player name
//   result.player_results[i].market_stats   → { player_aces, player_double_faults,
//                                              player_games_won, ... }
//
// GameContext (built via getGameContext) already extracts:
//   gc.homeScore / gc.awayScore    → sets won  (from result.score.scores.<side>.total)
//   gc.periodScores.<side>[period] → games in that set (raw pass-through)
//   gc.homeTeam / gc.awayTeam      → player display names
//   gc.isCompleted                 → match completed flag
// So resolvers use gc for score/team info and reach into `r.score.stats` /
// `r.player_results` only for the two things GameContext doesn't expose:
// team-level match stats (tiebreaks_won etc.) and per-player market_stats.

const readPeriodGames = (gc: GameContext, periodKey: string): { home: number; away: number } => ({
	home: (gc.periodScores.home as Record<string, number>)?.[periodKey] ?? 0,
	away: (gc.periodScores.away as Record<string, number>)?.[periodKey] ?? 0,
});

// Returns games won in a specific set for both players via GameContext, plus
// whether that set is FINISHED. gc.periodScores.home is already keyed by
// 'period_1' / 'period_2' / etc.
//
// "Finished" (not merely started) matters because resolvers also run against
// LIVE payloads, where period scores stream in game-by-game — "has any games"
// is true from a set's first game, which settled set markets mid-set
// (JWB-4521), once at a moment when the eventual set loser was ahead
// (JWB-4525). A set counts as finished when:
//   - its score is terminal: someone has 6+ games with a 2-game margin, or a
//     7-6 tiebreak; or
//   - the next set has begun (belt-and-braces for scoring quirks).
// Mid-set retirements do NOT count as finished — those markets void instead.
// A completed match with a deciding super-tiebreak can arrive from OO as 1-0
// (not a terminal set score). Count that set finished only when the match
// actually completed and did not end early.
const getSetGames = (
	gc: GameContext,
	periodKey: string
): { home: number; away: number; setFinished: boolean; started: boolean } => {
	const { home, away } = readPeriodGames(gc, periodKey);
	const started = home > 0 || away > 0;
	const hi = Math.max(home, away);
	const lo = Math.min(home, away);
	const terminalScore = hi >= 6 && (hi - lo >= 2 || (hi === 7 && lo === 6));
	const setNumber = Number(periodKey.split('_')[1]);
	const next = readPeriodGames(gc, `period_${setNumber + 1}`);
	const nextSetStarted = next.home > 0 || next.away > 0;
	return {
		home,
		away,
		started,
		setFinished:
			started && (terminalScore || nextSetStarted || (gc.isCompleted && !isAbnormalFixtureEnd(gc))),
	};
};

const resolveUnfinishedSet = (gc: GameContext): MarketOutcome => {
	if (isAbnormalFixtureEnd(gc)) return MarketOutcome.Void;
	if (gc.isCompleted) return MarketOutcome.Lost;
	return MarketOutcome.Pending;
};

const resolveTennisPlayerProp = (
	q: string,
	gc: GameContext,
	r: any,
	statKey: string,
	extractAfter: string[]
): [MarketOutcome, number?] => {
	const [outcome, value] = resolveGamePlayerProp(q, gc, r, statKey, extractAfter);
	if (outcome === MarketOutcome.Lost && isAbnormalFixtureEnd(gc))
		return [MarketOutcome.Void, value];
	return [outcome, value];
};

/**
 * Never derive a retirement winner from the set score: the player who quit can be the
 * one ahead, so guessing would invert the result. An unresolvable retirement returns
 * null and the caller voids instead of grading.
 */
const moneylineWinnerSide = (gc: GameContext): 'home' | 'away' | null => {
	if (gc.retirementInfo) return retirementWinnerSide(gc.retirementInfo, gc);
	if (gc.homeScore === gc.awayScore) return null;
	return gc.homeScore > gc.awayScore ? 'home' : 'away';
};

// Player-side lookup: which of home/away does the selected player match?
const sideForSelection = (
	q: string,
	gc: GameContext,
	extractAfter: string[]
): 'home' | 'away' | null => {
	const player = extractSelection(q, 'Will ', extractAfter);
	if (player === gc.homeTeam) return 'home';
	if (player === gc.awayTeam) return 'away';
	return null;
};

// Match-level (period='all') team stats accessor. Used for match-total tiebreaks,
// aces (team-side), etc. Reads r.score.stats.<side> which is an array with one
// entry per period; the period='all' entry aggregates the whole match.
const getMatchTeamStats = (r: any, side: 'home' | 'away'): Record<string, number> => {
	const bucket = r?.score?.stats?.[side];
	if (!Array.isArray(bucket)) return {};
	const all = bucket.find((p: any) => p?.period === 'all');
	return all?.stats ?? {};
};

// Sum games across all sets (pulled from GameContext.periodScores). Used for
// Total Games / Game Spread / Total Games Odd/Even.
const sumAllGames = (gc: GameContext): { home: number; away: number; total: number } => {
	const home = (Object.values(gc.periodScores.home) as number[]).reduce(
		(s, n) => s + (Number(n) || 0),
		0
	);
	const away = (Object.values(gc.periodScores.away) as number[]).reduce(
		(s, n) => s + (Number(n) || 0),
		0
	);
	return { home, away, total: home + away };
};

export const tennisConfig: Record<string, MarketConfig> = {
	// ── Main Lines ──────────────────────────────────────────────────────

	Moneyline: {
		buildQuestion: (m) => `Will ${m.selection} win?`,
		isFinalRequired: true,
		resolve: (q, gc) => {
			// Cancelled voids immediately: OO does not mark a cancelled fixture completed,
			// so this must outrank the completed gate below.
			if (isCancelledFixture(gc)) return [MarketOutcome.Void];
			if (!gc.isCompleted) return [MarketOutcome.Pending];
			// Walkover: OO still names who advances, but no match was played —
			// every question voids, including moneyline. Retirement grades.
			if (isWalkoverFixture(gc)) return [MarketOutcome.Void];
			const side = sideForSelection(q, gc, ['win']);
			if (!side) return [MarketOutcome.Lost];
			const winnerSide = moneylineWinnerSide(gc);
			// A retirement whose winner OO did not let us resolve is a refund, not a loss.
			if (!winnerSide) return [gc.retirementInfo ? MarketOutcome.Void : MarketOutcome.Lost];
			return [side === winnerSide ? MarketOutcome.Won : MarketOutcome.Lost];
		},
	},

	// ── Totals ──────────────────────────────────────────────────────────

	'Total Games': {
		buildQuestion: (m) =>
			`Will the match feature at least ${roundThresholdUp(m.points)} total games?`,
		hasProgressBar: true,
		isFinalRequired: true,
		resolve: (q, gc) => {
			const threshold = extractNumber(q);
			const { total } = sumAllGames(gc);
			return [
				resolveConditionAtEnd(total >= threshold, gc.isCompleted, isAbnormalFixtureEnd(gc)),
				total,
			];
		},
	},

	'Total Sets': {
		buildQuestion: (m) => `Will the match go at least ${roundThresholdUp(m.points)} sets?`,
		hasProgressBar: true,
		isFinalRequired: true,
		resolve: (q, gc) => {
			const threshold = extractNumber(q);
			const totalSets = gc.homeScore + gc.awayScore;
			return [
				resolveConditionAtEnd(totalSets >= threshold, gc.isCompleted, isAbnormalFixtureEnd(gc)),
				totalSets,
			];
		},
	},

	'1st Set Total Games': {
		buildQuestion: (m) => `Will the 1st set feature at least ${roundThresholdUp(m.points)} games?`,
		hasProgressBar: true,
		resolve: (q, gc) => {
			const threshold = extractNumber(q);
			const { home, away, setFinished } = getSetGames(gc, 'period_1');
			if (!setFinished) return [resolveUnfinishedSet(gc), home + away];
			return [resolveConditionAtEnd(home + away >= threshold, true, false), home + away];
		},
	},

	'2nd Set Total Games': {
		buildQuestion: (m) => `Will the 2nd set feature at least ${roundThresholdUp(m.points)} games?`,
		hasProgressBar: true,
		resolve: (q, gc) => {
			const threshold = extractNumber(q);
			const { home, away, setFinished } = getSetGames(gc, 'period_2');
			if (!setFinished) return [resolveUnfinishedSet(gc), home + away];
			return [resolveConditionAtEnd(home + away >= threshold, true, false), home + away];
		},
	},

	'3rd Set Total Games': {
		buildQuestion: (m) => `Will the 3rd set feature at least ${roundThresholdUp(m.points)} games?`,
		hasProgressBar: true,
		resolve: (q, gc) => {
			const threshold = extractNumber(q);
			const { home, away, setFinished } = getSetGames(gc, 'period_3');
			if (!setFinished) return [resolveUnfinishedSet(gc), home + away];
			return [resolveConditionAtEnd(home + away >= threshold, true, false), home + away];
		},
	},

	// ── Handicap ────────────────────────────────────────────────────────
	// OpticOdds format: name="Player X +1.5", selection="Player X", points=+1.5/-1.5.
	// Sign is baked into `points`; the resolver applies it to the selected side.

	'Game Spread': {
		buildQuestion: (m) => {
			const abs = Math.abs(m.points);
			return m.points < 0
				? `Will ${m.selection} win by at least ${Math.ceil(abs)} games?`
				: `Will ${m.selection} keep it within ${Math.floor(abs)} games?`;
		},
		isFinalRequired: true,
		resolve: (q, gc) => {
			// Cancelled voids immediately even though OO reports it as not completed.
			if (isAbnormalFixtureEnd(gc)) return [MarketOutcome.Void];
			if (!gc.isCompleted) return [MarketOutcome.Pending];
			const side = sideForSelection(q, gc, ['win by at least', 'keep it within']);
			if (!side) return [MarketOutcome.Lost];
			const threshold = extractNumber(q);
			const { home, away } = sumAllGames(gc);
			const selectedScore = side === 'home' ? home : away;
			const opponentScore = side === 'home' ? away : home;
			const won = q.includes('win by at least')
				? selectedScore - opponentScore >= threshold
				: opponentScore - selectedScore <= threshold;
			return [won ? MarketOutcome.Won : MarketOutcome.Lost];
		},
	},

	'Set Handicap': {
		buildQuestion: (m) => {
			const abs = Math.abs(m.points);
			return m.points < 0
				? `Will ${m.selection} win by at least ${Math.ceil(abs)} sets?`
				: `Will ${m.selection} keep it within ${Math.floor(abs)} sets?`;
		},
		isFinalRequired: true,
		resolve: (q, gc) => {
			// Cancelled voids immediately even though OO reports it as not completed.
			if (isAbnormalFixtureEnd(gc)) return [MarketOutcome.Void];
			if (!gc.isCompleted) return [MarketOutcome.Pending];
			const side = sideForSelection(q, gc, ['win by at least', 'keep it within']);
			if (!side) return [MarketOutcome.Lost];
			const threshold = extractNumber(q);
			const selectedScore = side === 'home' ? gc.homeScore : gc.awayScore;
			const opponentScore = side === 'home' ? gc.awayScore : gc.homeScore;
			const won = q.includes('win by at least')
				? selectedScore - opponentScore >= threshold
				: opponentScore - selectedScore <= threshold;
			return [won ? MarketOutcome.Won : MarketOutcome.Lost];
		},
	},

	// ── Set Props ───────────────────────────────────────────────────────

	'1st Set Moneyline': {
		buildQuestion: (m) => `Will ${m.selection} win the 1st set?`,
		resolve: (q, gc) => {
			const side = sideForSelection(q, gc, ['win the']);
			if (!side) return [MarketOutcome.Lost];
			const { home, away, setFinished } = getSetGames(gc, 'period_1');
			if (!setFinished) return [resolveUnfinishedSet(gc)];
			const winnerSide = home > away ? 'home' : 'away';
			return [side === winnerSide ? MarketOutcome.Won : MarketOutcome.Lost];
		},
	},

	'2nd Set Moneyline': {
		buildQuestion: (m) => `Will ${m.selection} win the 2nd set?`,
		resolve: (q, gc) => {
			const side = sideForSelection(q, gc, ['win the']);
			if (!side) return [MarketOutcome.Lost];
			const { home, away, setFinished } = getSetGames(gc, 'period_2');
			if (!setFinished) return [resolveUnfinishedSet(gc)];
			const winnerSide = home > away ? 'home' : 'away';
			return [side === winnerSide ? MarketOutcome.Won : MarketOutcome.Lost];
		},
	},

	'3rd Set Moneyline': {
		buildQuestion: (m) => `Will ${m.selection} win the 3rd set?`,
		resolve: (q, gc) => {
			const side = sideForSelection(q, gc, ['win the']);
			if (!side) return [MarketOutcome.Lost];
			const { home, away, setFinished } = getSetGames(gc, 'period_3');
			if (!setFinished) return [resolveUnfinishedSet(gc)];
			const winnerSide = home > away ? 'home' : 'away';
			return [side === winnerSide ? MarketOutcome.Won : MarketOutcome.Lost];
		},
	},

	'Both Players To Win A Set': {
		buildQuestion: () => `Will both players win at least one set?`,
		isFinalRequired: true,
		resolve: (_q, gc) => [
			resolveConditionAtEnd(
				gc.homeScore >= 1 && gc.awayScore >= 1,
				gc.isCompleted,
				isAbnormalFixtureEnd(gc)
			),
		],
	},

	// ── Match Props ─────────────────────────────────────────────────────

	'Total Games Odd/Even': {
		buildQuestion: () => `Will the total games in the match be odd?`,
		isFinalRequired: true,
		resolve: (_q, gc) => {
			// Cancelled voids immediately even though OO reports it as not completed.
			if (isAbnormalFixtureEnd(gc)) return [MarketOutcome.Void];
			if (!gc.isCompleted) return [MarketOutcome.Pending];
			const { total } = sumAllGames(gc);
			return [total % 2 === 1 ? MarketOutcome.Won : MarketOutcome.Lost];
		},
	},

	// Match-level tiebreak count. Each tiebreak has one winner, so the sum of
	// `tiebreaks_won` across home + away equals the total tiebreaks played.
	'Total Tie Breaks': {
		buildQuestion: (m) =>
			`Will the match feature at least ${roundThresholdUp(m.points)} tiebreak(s)?`,
		hasProgressBar: true,
		isFinalRequired: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const home = getMatchTeamStats(r, 'home').tiebreaks_won ?? 0;
			const away = getMatchTeamStats(r, 'away').tiebreaks_won ?? 0;
			const total = home + away;
			return [
				resolveConditionAtEnd(total >= threshold, gc.isCompleted, isAbnormalFixtureEnd(gc)),
				total,
			];
		},
	},

	// OpticOdds Correct Score format (verified against upcoming ATP odds):
	// selection="Player X", selection_line="3:2". Question renders with the score
	// baked in and resolver matches both winner AND score.
	'Correct Score': {
		buildQuestion: (m) =>
			`Will the match end with ${m.selection} winning ${m.selection_line ?? m.name}?`,
		// OpticOdds-only: needs selection + selection_line (score) the LLM never sends. Excluded
		// from LLM generation so it can't produce a player-less "…end with  winning ?" (JWB-4739).
		llmGeneratable: false,
		isFinalRequired: true,
		resolve: (q, gc) => {
			// Cancelled voids immediately even though OO reports it as not completed.
			if (isAbnormalFixtureEnd(gc)) return [MarketOutcome.Void];
			if (!gc.isCompleted) return [MarketOutcome.Pending];
			// No resolvable player or no score in the question means the market is malformed
			// (e.g. an empty selection) — refund it rather than assert a Lost (JWB-4739).
			const side = sideForSelection(q, gc, ['winning']);
			if (!side) return [MarketOutcome.Void];
			const scoreMatch = q.match(/(\d):(\d)/);
			if (!scoreMatch) return [MarketOutcome.Void];
			const selectedScore = Number(scoreMatch[1]);
			const opponentScore = Number(scoreMatch[2]);
			const actualSelected = side === 'home' ? gc.homeScore : gc.awayScore;
			const actualOpponent = side === 'home' ? gc.awayScore : gc.homeScore;
			return [
				actualSelected === selectedScore && actualOpponent === opponentScore
					? MarketOutcome.Won
					: MarketOutcome.Lost,
			];
		},
	},

	// ── Player Props ────────────────────────────────────────────────────
	// Stat keys verified against OpticOdds /fixtures/player-results:
	//   market_stats.player_aces / .player_double_faults / .player_games_won
	// Uses the shared getPlayerResult which reads r.player_results — the same
	// key MLB uses on the stored SSE payload.

	'Player Aces': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${roundThresholdUp(m.points)} aces?`,
		hasProgressBar: true,
		isFinalRequired: true,
		resolve: (q, gc, r) => resolveTennisPlayerProp(q, gc, r, 'player_aces', ['record at least']),
	},

	'Player Double Faults': {
		buildQuestion: (m) =>
			`Will ${m.selection} commit at least ${roundThresholdUp(m.points)} double faults?`,
		hasProgressBar: true,
		isFinalRequired: true,
		resolve: (q, gc, r) =>
			resolveTennisPlayerProp(q, gc, r, 'player_double_faults', ['commit at least']),
	},

	'Player Games Won': {
		buildQuestion: (m) =>
			`Will ${m.selection} win at least ${roundThresholdUp(m.points)} games in the match?`,
		hasProgressBar: true,
		isFinalRequired: true,
		resolve: (q, gc, r) => resolveTennisPlayerProp(q, gc, r, 'player_games_won', ['win at least']),
	},
};
